import './UploadStudentList.scss';
import '../../OrganizationModals.scss';
import { Modal } from '@trackman/web-shared-components';
import { useGetClasses } from 'queryHooks/Organization/Classes/useGetClasses';
import { useGetNumberOfInvitesLeft } from 'queryHooks/Organization/Invites/useGetNumberOfInvitesLeft';
import { Trans, useTranslation } from 'react-i18next';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { useStudentsStore } from 'stores/Organization/StudentsStore';

import { ReactComponent as DocumentIcon } from 'images/icons/menu/Document.svg';
import { NewInvite } from 'types/organization/invites';
import { useState } from 'react';
import { useSubmitInvites } from 'queryHooks/Organization/Invites/useSubmitInvites';
import Separator from 'components/Separator/Separator';
import DownloadBar from 'components/DownloadBar/DownloadBar';
import { Dropzone } from 'components/Dropzone/Dropzone';
import { useExtractStudentsFromCSV } from 'queryHooks/Organization/Invites/useExtractStudentsFromCSV';

import ErrorInfo from 'components/ErrorInfo/ErrorInfo';
import { isValidEmail } from 'utilities/inputValidation';
import { downloadStudentListTemplate } from 'utilities/studentListTemplate';
import { NoInvitesLeft } from '../../Invites/NoInvitesLeft/NoInvitesLeft';
import { NumberOfInvitesLeft } from '../../Invites/NumberOfInvitesLeft/NumberOfInvitesLeft';
import { NewInvitesList } from '../../Invites/NewInvitesList/NewInvitesList';

type Props = {
  isOpen: boolean;
  close: () => void;
};

export const UploadStudentList = ({ isOpen, close }: Props) => {
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);
  const selectedOrganizationName = useOrganizationStore((state) => state.selectedOrganizationName);
  const selectedClassId = useStudentsStore((state) => state.selectedClassId);
  const setSelectedClassId = useStudentsStore((state) => state.setSelectedClassId);

  useGetClasses(selectedOrganizationId, false, (selectedClassId) => setSelectedClassId(selectedClassId));
  const { data: classList } = useGetClasses(selectedOrganizationId, false, (selectedClassId) => setSelectedClassId(selectedClassId));
  const { data: numberOfInvitesLeftFromApi, isPending: isNumberOfInvitesLeftPending } = useGetNumberOfInvitesLeft(selectedOrganizationId);
  const getSelectedClassName = classList?.find((c) => c.id === selectedClassId)?.name ?? '';

  const [newInvites, setNewInvites] = useState<NewInvite[]>([]);

  const submitInvites = useSubmitInvites(selectedOrganizationId, { onSuccess: () => close() });

  return (
    <>
      {!isNumberOfInvitesLeftPending ? (
        <UploadStudentListModal
          isOpen={isOpen}
          selectedClassId={selectedClassId}
          selectedClassName={getSelectedClassName}
          selectedOrganizationName={selectedOrganizationName}
          numberOfInvitesLeftFromApi={numberOfInvitesLeftFromApi || 0}
          newInvites={newInvites}
          isPending={submitInvites.isPending}
          confirm={() => submitInvites.mutate(newInvites)}
          removeNewInvite={setNewInvites}
          close={close}
          addNewInvites={setNewInvites}
        />
      ) : (
        <></>
      )}
    </>
  );
};

type ModalProps = Props & {
  selectedClassId: string;
  selectedClassName: string;
  selectedOrganizationName: string;
  numberOfInvitesLeftFromApi: number;
  newInvites: NewInvite[];
  isPending: boolean;
  confirm: () => void;
  removeNewInvite: (updatedInvites: NewInvite[]) => void;
  addNewInvites: (newInvites: NewInvite[]) => void;
};

const UploadStudentListModal = ({
  isOpen,
  selectedClassId,
  selectedClassName,
  selectedOrganizationName,
  numberOfInvitesLeftFromApi,
  close,
  confirm,
  newInvites,
  isPending,
  removeNewInvite,
  addNewInvites,
}: ModalProps) => {
  const { t } = useTranslation();
  const [numberOfInvitesLeft, setNumberOfInvitesLeft] = useState<number>(numberOfInvitesLeftFromApi || 0);
  const [studentsExtractionErrors, setStudentExtractionErrors] = useState<string[]>([]);
  const extractStudentsFromCSV = useExtractStudentsFromCSV({ onSuccess: (response) => handleDataExtraction(response) });

  const handleDataExtraction = (extractedInvites: NewInvite[]) => {
    const updatedInvites = [
      ...newInvites,
      ...extractedInvites.map((i) => ({
        ...i,
        classes: [{ id: selectedClassId, name: selectedClassName }],
        organisationName: selectedOrganizationName,
      })),
    ];

    const updatedNumberOfInvitesLeft = numberOfInvitesLeft - extractedInvites.length;
    addNewInvites(updatedInvites);
    setNumberOfInvitesLeft(updatedNumberOfInvitesLeft);
    validateExtractedInvites(updatedInvites, updatedNumberOfInvitesLeft);
  };

  const validateExtractedInvites = (updatedNewInvites: NewInvite[], updatedNumberOfInvitesLeft: number) => {
    setStudentExtractionErrors([]);

    if (updatedNumberOfInvitesLeft < 0) {
      setStudentExtractionErrors((errorMsg) => [...errorMsg, t('YouHaveReachedTheLimitOfLicenses', { ns: 'studentsPage' })]);
    }

    updatedNewInvites.forEach((invite) => {
      if (!isValidEmail(invite.email)) {
        setStudentExtractionErrors((errorMsg) => [...errorMsg, t('EmailIsNotValid', { ns: 'studentsPage', email: invite.email })]);
      }

      if (updatedNewInvites.filter((i) => i.email === invite.email).length > 1) {
        setStudentExtractionErrors((errorMsg) => [
          ...errorMsg,
          t('EmailIsAddedMultipleTimes', { ns: 'studentsPage', email: invite.email }),
        ]);
      }
    });
  };

  const handleRemoveNewInvite = (email: string, index: number) => {
    const removedInvite = newInvites.at(index);
    const updatedInvites = newInvites.filter((_, i) => i !== index);
    const updatedNumberOfInvitesLeft = numberOfInvitesLeft + 1;

    if (removedInvite) {
      validateExtractedInvites(updatedInvites, updatedNumberOfInvitesLeft);

      removeNewInvite(updatedInvites);
      setNumberOfInvitesLeft(updatedNumberOfInvitesLeft);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => close()}
      className='organization-modal upload-modal'
      title={t('UploadStudentList', { ns: 'studentsPage' })}
      titleIcon={<DocumentIcon />}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => close(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SendInvitation', { ns: 'studentsPage' }),
        onClick: () => confirm(),
        disabled: numberOfInvitesLeftFromApi === 0 || !newInvites.length || isPending || studentsExtractionErrors.length > 0,
      }}
    >
      <div className='modal-container'>
        {numberOfInvitesLeftFromApi === 0 ? (
          <NoInvitesLeft />
        ) : (
          <>
            {newInvites.length === 0 ? (
              <div className='steps-container'>
                <UploadFirstStep />
                <Separator />
                <UploadSecondStep handleDataExtraction={extractStudentsFromCSV.mutate} />
              </div>
            ) : (
              <>
                <ExtractedInvites
                  newInvites={newInvites}
                  extractionErrors={studentsExtractionErrors}
                  deleteNewInvite={handleRemoveNewInvite}
                />
              </>
            )}
            <NumberOfInvitesLeft invitesLeft={numberOfInvitesLeft} />
          </>
        )}
      </div>
    </Modal>
  );
};

const UploadFirstStep = () => {
  const { t } = useTranslation();

  return (
    <div className='step'>
      <span className='header'>
        <Trans i18nKey='Step1PrepareStudentList' ns='studentsPage'>
          <strong>Step 1</strong> Prepare student list using template
        </Trans>
      </span>
      <DownloadBar
        subtitle={t('StudentList', { ns: 'studentsPage' })}
        description={t('DownloadTemplateHere', { ns: 'studentsPage' })}
        iconSubtitle={t('CSV', { ns: 'studentsPage' })}
        onClick={downloadStudentListTemplate}
      />
    </div>
  );
};

const UploadSecondStep = ({ handleDataExtraction }: { handleDataExtraction: (file: File) => void }) => {
  return (
    <div className='step'>
      <span className='header'>
        <Trans i18nKey='Step2UploadTheStudentList' ns='studentsPage'>
          <strong>Step 2</strong> Upload the Student list
        </Trans>
      </span>
      <Dropzone handleDataExtraction={handleDataExtraction} />
    </div>
  );
};

const ExtractedInvites = ({
  newInvites,
  extractionErrors,
  deleteNewInvite,
}: {
  newInvites: NewInvite[];
  extractionErrors: string[];
  deleteNewInvite: (email: string, index: number) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className='step extracted-invites'>
      <span className='header'>{t('DoubleCheckTheStudentList', { ns: 'studentsPage' })}</span>
      {extractionErrors.length > 0
        ? extractionErrors.map((errorMsg, index) => <ErrorInfo key={`error-${index}`} errorMsg={errorMsg} position='static' />)
        : null}
      <NewInvitesList newInvitesList={newInvites} deleteNewInvite={deleteNewInvite} />
    </div>
  );
};
